import { FC, useState, useCallback } from "react";
import styles from "./PopupTelegramChat.module.css";
import { Button } from "../Button";
import { ButtonGroup } from "../ButtonGroup";
import { Popup } from "../Popup";
import PopupStructure from "../PopupStructure";
import { PopupTitle } from "../PopupTitle";
import SelectField from "../SelectField";
import Icon from "../Icon";
import { TelegramChat, useUpdateTelegramChatModeMutation, useGetChannelsQuery } from "../../features/api/apiSlice";
import { IOption } from "../SelectField/SelectField.interface";

interface PopupTelegramChatProps {
  onClose: () => void;
  telegramChat: TelegramChat;
  channelId: number;
  onDisconnect?: () => void;
}

const filterOptions: IOption[] = [
  { value: "all", label: "Все сообщения" },
  { value: "none", label: "Не собирать сообщения" },
  { value: "last", label: "Из последнего поста" },
  { value: "select", label: "Выбрать конкретный пост" },
];

const PopupTelegramChat: FC<PopupTelegramChatProps> = ({ onClose, telegramChat, channelId, onDisconnect }) => {
  const { refetch } = useGetChannelsQuery();
  const [updateTelegramChatMode, { isLoading }] = useUpdateTelegramChatModeMutation();

  const [searchValue, setSearchValue] = useState("");
  const [postSearchValue, setPostSearchValue] = useState("");

  const [selectedPost, setSelectedPost] = useState<IOption | null>(() => {
    const post = telegramChat.posts.find(p => p.post_id === telegramChat.chat_mode);
    if (post) {
      return {
        value: post.post_id,
        label: post.title || post.channel_title
      };
    }
    return null;
  });

  const [selectedFilter, setSelectedFilter] = useState<IOption | null>(() => {
    const currentChatMode = telegramChat.chat_mode;

    if (!currentChatMode) {
      return filterOptions.find(option => option.value === "all") || null;
    }

    if (telegramChat.posts.some(p => p.post_id === currentChatMode)) {
      return filterOptions.find(option => option.value === "select") || null;
    }

    return filterOptions.find(option => option.value === currentChatMode) || null;
  });

  const postOptions: IOption[] = telegramChat.posts.map(post => ({
    value: post.post_id,
    label: post.title || post.channel_title
  }));

  const handleSave = useCallback(() => {
    if (isLoading) {
      return;
    }

    let chatMode = "all";

    if (selectedFilter?.value === "select" && selectedPost) {
      chatMode = selectedPost.value;
    } else if (selectedFilter?.value) {
      chatMode = selectedFilter.value;
    }

    updateTelegramChatMode({
      id: channelId,
      chat_mode: chatMode
    })
      .unwrap()
      .then(() => refetch())
      .then(() => onClose())
      .catch(error => {
        console.error('Failed to update chat mode:', error);
      });
  }, [channelId, isLoading, onClose, refetch, selectedFilter, selectedPost, updateTelegramChatMode]);

  const headerComponent = (
    <PopupTitle>
      Настройки чата Telegram
    </PopupTitle>
  );

  const contentComponent = (
    <>
      <div className={styles["popup-telegram-chat__info-block"]}>
        <div className={styles["popup-telegram-chat__icon"]}>
          <Icon name="telegram" />
        </div>
        <div className={styles["popup-telegram-chat__info"]}>
          <span className={styles["popup-telegram-chat__group-name"]}>
            {telegramChat.title}
          </span>
          <span className={styles["popup-telegram-chat__status"]}>
            Подключен
          </span>
        </div>
        <Button
          kind="primary"
          className={styles["popup-telegram-chat__button"]}
          onClick={onDisconnect}
          disabled={isLoading}
        >
          Отключить
        </Button>
      </div>

      <div className={styles["popup-telegram-chat__heading"]}>
        Фильтр сообщений
      </div>
      <div className={styles["popup-telegram-chat__section"]}>
        <div className={styles["popup-telegram-chat__select"]}>
          <SelectField
            placeholder="Выберите тип фильтрации"
            selectedOption={selectedFilter}
            selectedOptionHandler={setSelectedFilter}
            value={searchValue}
            setValue={setSearchValue}
            options={filterOptions}
            isSearchable={false}
          />
        </div>

        {selectedFilter?.value === "select" && (
          <div className={styles["popup-telegram-chat__select"]}>
            <SelectField
              placeholder="Выберите пост"
              selectedOption={selectedPost}
              selectedOptionHandler={setSelectedPost}
              value={postSearchValue}
              setValue={setPostSearchValue}
              options={postOptions}
              isSearchable={false}
            />
          </div>
        )}
      </div>
    </>
  );

  const footerComponent = (
    <ButtonGroup className={styles["popup-telegram-chat__footer"]}>
      <Button
        kind="secondary"
        onClick={onClose}
        disabled={isLoading}
      >
        Отмена
      </Button>
      <Button
        kind="primary"
        onClick={handleSave}
        disabled={isLoading || (selectedFilter?.value === "select" && !selectedPost)}
      >
        Сохранить
      </Button>
    </ButtonGroup>
  );

  return (
    <Popup isShow={true} maxWidth="530px" onClose={onClose}>
      <PopupStructure
        headerComponent={headerComponent}
        contentComponent={contentComponent}
        footerComponent={footerComponent}
      />
    </Popup>
  );
};

export default PopupTelegramChat;